import "../../assets/admin-themes.css";
import { useContext, useState, useMemo, useEffect, useRef } from "react";
import { ThemesDataContext } from "../../providers/theme-management-provider";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tree } from "primereact/tree";
import { TreeNode } from "primereact/treenode";
import { FormFieldDto, ThemeNode, CssProperty } from "../../general/dtos";
import { BASIC_COLORS, extractColorFromValue, isColor, isColorProperty, PRIMEREACT_THEME_VARS, SAFE_CORE_CSS, UNIT_SUGGESTIONS, VALUE_SUGGESTIONS } from "../../general/utilities";
import { LongTextTooltip } from '../long-text-tooltip';
import { PREVIEW_CONTENT } from "./preview-content";
import { renderFragmentHtml } from "../content-viewer/rendering-helpers";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { AutoComplete } from "primereact/autocomplete";
import { EssentialsDialog } from "./essentials-dialog";
import { InputsDialog } from "./inputs-dialog";
import { TypographyDialog } from "./typography-dialog";
import { ManageImportsDialog } from "./imports-dialog";
import { BodyStylesDialog } from "./body-dialog";
import { Menu } from "primereact/menu";

const showHidden: boolean = false;

export function ThemeEditorPane() {
    const { loading, mode, selectedTheme, cssSelectors, generatedCss, updateThemeName, revertChanges, saveTheme,
        bulkUpdateCssProperties } = useContext(ThemesDataContext)!;
    const menuRef = useRef<Menu>(null);
    const [selectedNodeKey, setSelectedNodeKey] = useState<any>(null);
    const [filterText, setFilterText] = useState<string>("");
    const [filteredPropertySuggestions, setFilteredPropertySuggestions] = useState<string[]>([]);
    const [filteredValueSuggestions, setFilteredValueSuggestions] = useState<string[]>([]);
    const [showEssentials, setShowEssentials] = useState<boolean>(false);
    const [showInputs, setShowInputs] = useState<boolean>(false);
    const [showTypography, setShowTypography] = useState<boolean>(false);
    const [showManageImports, setShowManageImports] = useState<boolean>(false);
    const [showBodyDialog, setShowBodyDialog] = useState<boolean>(false);

    const [localProperties, setLocalProperties] = useState<CssProperty[]>([]);
    
    // Stable ref for the data so search functions always have fresh data without re-binding
    const localPropertiesRef = useRef(localProperties);
    useEffect(() => { localPropertiesRef.current = localProperties; }, [localProperties]);

    const treeNodes = useMemo(() => {
        const safeSelectors = Array.isArray(cssSelectors) ? cssSelectors : [];
        const mapNodes = (nodes: ThemeNode[]): TreeNode[] => {
            return nodes
                .filter(node => showHidden || !node.selector.startsWith('__'))
                .map((node) => ({
                    key: node.selector,
                    label: node.selector,
                    data: node,
                    children: node.children?.length > 0 ? mapNodes(node.children) : undefined
                }));
        };

        return safeSelectors
            .filter(category => showHidden || category.title !== '__hidden')
            .map((category) => ({
                key: category.title,
                label: category.title,
                children: mapNodes(category.children)
            }));
    }, [cssSelectors]);

    const sortProperties = (properties: CssProperty[]): CssProperty[] => {
        return (properties ?? []).sort((a, b) => a.property.toLowerCase().localeCompare(b.property.toLowerCase()));
    };

    const selectedSelector = useMemo(() => {
        if (!selectedNodeKey) return null;

        const findNode = (nodes: ThemeNode[]): ThemeNode | null => {
            for (const node of nodes) {
                if (node.selector === selectedNodeKey) return node;
                if (node.children) {
                    const found = findNode(node.children);
                    if (found) return found;
                }
            }
            return null;
        };

        for (const cat of cssSelectors) {
            const found = findNode(cat.children);
            if (found) return found;
        }
        return null;
    }, [selectedNodeKey, cssSelectors]);

    useEffect(() => {
        if (selectedSelector?.properties) {
            const initializedProperties = selectedSelector.properties.map(p => ({
                ...p,
                id: (p as any).id || crypto.randomUUID()
            }));

            setLocalProperties([...sortProperties(initializedProperties)]);
        } else {
            setLocalProperties([]);
        }
    }, [selectedNodeKey, selectedSelector]);

    const syncToProvider = () => {
        if (selectedSelector) {
            bulkUpdateCssProperties(selectedSelector.selector, localProperties, 'replace');
        }
    };

    const handleNodeChange = (newKey: string) => {
        syncToProvider();
        setSelectedNodeKey(newKey);
    };

    const handleSave = async (): Promise<void> => {
        syncToProvider();
        await saveTheme();
    };

    const handleCancel = (): void => {
        revertChanges();
    };

    const handleLocalUpdate = (id: string, key: keyof CssProperty, value: string) => {
        setLocalProperties(prev => prev.map(p =>
            (p as any).id === id ? { ...p, [key]: value } : p
        ));
    };

    const handleLocalDelete = (id: string) => {
        setLocalProperties(prev => prev.filter(p => (p as any).id !== id));
    };

    const handleLocalAdd = () => {
        const newItem = { property: '', value: '', id: crypto.randomUUID() };
        setLocalProperties([...localProperties, newItem]);
    };

    const nodeTemplate = (node: TreeNode) => {
        return (
            <span
                className="tree-node-item"
                data-markdown-tooltip={node.label}
                data-pr-position="top"
                style={{
                    display: 'block',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '250px',
                    cursor: 'pointer'
                }}
            >
                {node.label}
            </span>
        );
    };

    const filteredTreeNodes = useMemo(() => {
        if (!filterText.trim()) return treeNodes;

        const lowerFilter = filterText.toLowerCase();

        const filterNodes = (nodes: TreeNode[]): TreeNode[] => {
            return nodes
                .filter(node => showHidden || !node.key!.toString().startsWith('__'))
                .map(node => {
                    const filteredChildren = node.children ? filterNodes(node.children) : undefined;
                    const matches = node.label?.toLowerCase().includes(lowerFilter);
                    const hasMatchingChildren = filteredChildren && filteredChildren.length > 0;

                    if (matches || hasMatchingChildren) {
                        return {
                            ...node,
                            children: filteredChildren,
                            expanded: true
                        };
                    }
                    return null;
                })
                .filter((node): node is NonNullable<typeof node> => node !== null);
        };

        return filterNodes(treeNodes);
    }, [treeNodes, filterText]);

    function attachCollapsibleListener(doc: Document): void {
        doc.addEventListener("click", (e: MouseEvent) => {
            const target = e.target as HTMLElement | null
            if (!target) return

            const header = target.closest(".collapsible-header")
            if (!header) return

            const parent = header.parentElement
            if (!parent) return

            parent.classList.toggle("collapsed")
        });
    }

    const iframeLoaded = async (e: any): Promise<void> => {
        const doc = e.currentTarget.contentDocument;
        try {
            const url = "/assets/editor-utilities.css";
            if (!doc.querySelector(`link[href="${url}"]`)) {
                const link = doc.createElement("link");
                link.rel = "stylesheet";
                link.href = url;
                doc.head.appendChild(link);
            }
        } catch (err) {
            console.log(err);
        }

        const contextMenuScript = doc.createElement("script");
        contextMenuScript.textContent = `document.addEventListener('contextmenu', e => e.preventDefault());`;
        doc.body.appendChild(contextMenuScript);

        attachCollapsibleListener(doc);
    }

    const getPreviewHtml = (): string => {
        const formFields: FormFieldDto[] = [];
        const textField: FormFieldDto = {
            key: '9308002b-ef13-480a-82b5-ddc1eb67f018',
            type: 'text',
            label: 'Text',
            required: false,
            placeholder: 'Please write here',
            tooltip: 'A sample text input',
            expectedValue: null
        };
        formFields.push(textField);
        const selectField: FormFieldDto = {
            key: '068c7fa7-b25f-444e-9004-a93c40501b51',
            type: 'select',
            label: 'Select',
            required: false,
            placeholder: 'Please select',
            tooltip: 'A sample select input',
            expectedValue: null,
            options: [{ key: '1', value: 'Option one' }, { key: '2', value: 'Option two' }]
        };
        formFields.push(selectField);
        const checkField: FormFieldDto = {
            key: 'd3e48880-efbc-48ff-829a-87b17bb85ba8',
            type: 'checkbox',
            label: 'Check',
            required: false,
            placeholder: 'Please check',
            tooltip: 'A sample checkbox input',
            expectedValue: null
        };
        formFields.push(checkField);
        const radioField: FormFieldDto = {
            key: 'd3e48880-efbc-48ff-829a-87b17bb85bb8',
            type: 'radio',
            label: 'Radio',
            required: false,
            placeholder: 'Please select',
            tooltip: 'A sample radio input',
            expectedValue: null,
            options: [{ key: '1', value: 'Option one' }, { key: '2', value: 'Option two' }]
        };
        formFields.push(radioField);
        const css: string = generatedCss;
        return renderFragmentHtml(`<html><body><style>${css}</style>${PREVIEW_CONTENT}</body></html>`, formFields);
    };

    const designMenuItems = [
        { label: 'Essentials', icon: 'pi pi-palette', command: () => setShowEssentials(true) },
        { label: 'Inputs', icon: 'pi pi-sliders-h', command: () => setShowInputs(true) },
        { label: 'Typography', icon: 'pi pi-book', command: () => setShowTypography(true) },
        { label: 'Imports', icon: 'pi pi-link', command: () => setShowManageImports(true) },
        { label: 'Body', icon: 'pi pi-desktop', command: () => setShowBodyDialog(true) }
    ];

    return (<>
        <LongTextTooltip target=".tree-node-item" className="editor-tooltip-override" />
        <header className="theme-editor-header" style={{ borderBottom: '1px solid var(--surface-200)' }}>
            {mode === 'view' ? (
                <h2 style={{ margin: '5px 0 5px 10px' }}>{selectedTheme?.label}</h2>
            ) : (
                <div className="flex align-items-center gap-2">
                    <label>Name:</label>
                    <InputText
                        value={selectedTheme?.label ?? ''}
                        onChange={(e) => {
                            updateThemeName(e.target.value);
                        }}
                        required
                        disabled={loading}
                    />
                </div>
            )}
        </header>
        <div className="editor-container" style={{ display: 'flex', gap: '0.1rem', height: '100vh', padding: '0.1rem' }}>
            <div className="pane" style={{ flex: '0 0 20%' }}>
                <h3>Selectors</h3>
                <div className="p-input-icon-left p-input-icon-right w-full mb-3">
                    <i className="pi pi-search" />
                    <InputText
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                        placeholder="Search selectors..."
                        className="w-full p-inputtext-sm"
                        disabled={loading}
                    />
                    {filterText && (
                        <i className="pi pi-times cursor-pointer" onClick={() => setFilterText("")} />
                    )}
                </div>
                <Tree
                    value={filteredTreeNodes}
                    nodeTemplate={nodeTemplate}
                    selectionMode="single"
                    selectionKeys={selectedNodeKey}
                    onSelectionChange={(e) => handleNodeChange(e.value as string)}
                    style={{ height: 'calc(100vh - 100px)', overflow: 'auto', fontSize: '0.85rem' }}
                    disabled={loading}
                />
            </div>

            <div className="pane" style={{ flex: '1' }}>
                <h3>Properties</h3>
                <DataTable
                    key={`${selectedSelector?.selector ?? 'none'}-${mode}`}
                    dataKey="id"
                    value={localProperties}
                    scrollable
                    scrollHeight="flex"
                    size="small"
                    disabled={loading}
                >
                    <Column
                        header="Property"
                        body={(rowData: CssProperty) => (
                            <AutoComplete
                                key={(rowData as any).id}
                                value={rowData.property}
                                suggestions={filteredPropertySuggestions}
                                completeMethod={(e) => {
                                    const query = e.query.toLowerCase();
                                    const existingKeys = localPropertiesRef.current.map(p => p?.property?.toLowerCase() ?? '').filter(Boolean);
                                    const results = [...SAFE_CORE_CSS].filter(item => 
                                        item.toLowerCase().includes(query) && !existingKeys.includes(item.toLowerCase())
                                    );
                                    setFilteredPropertySuggestions(results);
                                }}
                                onChange={(e) => {
                                    // flushSync(() => {
                                    //     handleLocalUpdate((rowData as any).id, 'property', e.value);
                                    // });
                                    setTimeout(() => {
                                        handleLocalUpdate((rowData as any).id, 'property', e.value);
                                    }, 0);
                                }}
                                readOnly={mode === 'view'}
                                className="w-full"
                                inputClassName="w-full p-inputtext-sm font-mono border-none bg-transparent shadow-none"
                                appendTo="self"
                                minLength={1}
                                delay={0}
                                placeholder="CSS property..."
                                virtualScrollerOptions={{ itemSize: 30 }}
                            />
                        )}
                    />
                    <Column
                        header="Value"
                        body={(rowData: CssProperty) => {
                            const iscolor = isColor(rowData.property, rowData.value);
                            const extractedColor = extractColorFromValue(rowData.value);
                            const itemTemplate = (item: string) => {
                                const isVar = item.startsWith('var(');
                                return (
                                    <div className="flex align-items-center gap-2 py-1">
                                        <div 
                                            style={{ backgroundColor: item, width: '12px', height: '12px' }} 
                                            className="border-round-sm border-1 border-black-alpha-20" 
                                        />
                                        <span className={`text-xs font-mono ${isVar ? 'text-primary font-bold' : ''}`}>
                                            {item}
                                        </span>
                                    </div>
                                );
                            };
                            return (
                                <div className="relative flex align-items-center w-full">
                                    {(iscolor || extractedColor) && rowData.value && (
                                        <div 
                                            style={{ 
                                                backgroundColor: rowData.value, 
                                                width: '14px', height: '14px', 
                                                border: '1px solid #ddd',
                                                position: 'absolute', left: '8px', zIndex: 1
                                            }} 
                                            className="border-round-sm pointer-events-none"
                                        />
                                    )}
                                    <AutoComplete
                                        key={(rowData as any).id}
                                        value={rowData.value}
                                        suggestions={filteredValueSuggestions}
                                        completeMethod={(e) => {
                                            const query = e.query.toLowerCase();
                                            const key = rowData.property.toLowerCase();
                                            let baseList = UNIT_SUGGESTIONS;
                                            if (isColorProperty(key)) {
                                                baseList = [...PRIMEREACT_THEME_VARS.map(v => `var(${v})`), ...BASIC_COLORS];
                                            } else if (VALUE_SUGGESTIONS[key]) {
                                                baseList = VALUE_SUGGESTIONS[key];
                                            }
                                            setFilteredValueSuggestions(baseList.filter(val => val.toLowerCase().includes(query)));
                                        }}
                                        onChange={(e) => {
                                            // flushSync(() => {
                                            //     handleLocalUpdate((rowData as any).id, 'value', e.value);
                                            // });
                                            setTimeout(() => {
                                                handleLocalUpdate((rowData as any).id, 'value', e.value);
                                            }, 0);
                                        }}
                                        itemTemplate={itemTemplate}
                                        readOnly={mode === 'view'}
                                        className="w-full"
                                        inputClassName="w-full p-inputtext-sm font-mono border-none bg-transparent shadow-none"
                                        inputStyle={{ paddingLeft: (iscolor || extractedColor) ? '28px' : '8px' }}
                                        appendTo="self"
                                        delay={0}
                                        virtualScrollerOptions={{ itemSize: 30 }}
                                    />
                                </div>
                            );
                        }}
                    />
                    {mode !== 'view' && (
                        <Column
                            header="Actions"
                            body={(rowData: CssProperty) => (
                                <Button
                                    icon="pi pi-trash"
                                    className="p-button-text p-button-danger p-button-sm"
                                    onClick={() => handleLocalDelete((rowData as any).id)}
                                />
                            )}
                            style={{ width: '50px' }}
                        />
                    )}
                </DataTable>
                {mode !== 'view' && selectedSelector && (
                    <Button
                        label="Add new style item"
                        icon="pi pi-plus"
                        className="p-button-text p-button-sm mt-2"
                        onClick={handleLocalAdd}
                        disabled={loading}
                    />
                )}
                <div className="selected-selector-footer" style={{
                    padding: '0.5rem',
                    marginTop: '0.5rem',
                    background: 'var(--surface-50)',
                    border: '1px solid var(--surface-200)',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    color: selectedSelector ? 'var(--text-color)' : 'var(--text-color-secondary)'
                }}>
                    <strong>Selected selector:</strong> {selectedSelector ? selectedSelector.selector : 'None selected'}
                </div>
            </div>

            <div className="pane" style={{ flex: '0 0 50%' }}>
                <h3>Live preview</h3>
                <iframe
                    title="Theme preview"
                    srcDoc={getPreviewHtml()}
                    style={{ width: '100%', height: 'calc(100vh - 100px)', border: '1px solid #ccc' }}
                    sandbox="allow-scripts allow-same-origin"
                    onLoad={iframeLoaded}
                />
            </div>
        </div>
        {mode !== 'view' && (
            <div className="admin-toolbar flex justify-content-end gap-2 mt-3 p-2 border-top-1 surface-border">
                <Menu model={designMenuItems} popup ref={menuRef} id="design_menu" />
                <Button
                    label="Design"
                    icon="pi pi-palette"
                    onClick={(event) => menuRef.current?.toggle(event)}
                    aria-controls="design_menu"
                    aria-haspopup
                    className="p-button-secondary"
                    disabled={loading}
                >
                    <span className="pi pi-chevron-down ml-2" style={{ fontSize: '0.8rem' }}></span>
                </Button>
                <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={handleCancel} disabled={loading} />
                <Button label="Save" icon="pi pi-check" className="p-button-primary" onClick={handleSave} disabled={loading} />
            </div>
        )}

        <TypographyDialog visible={showTypography} onHide={() => setShowTypography(false)} />
        <InputsDialog visible={showInputs} onHide={() => setShowInputs(false)} />
        <EssentialsDialog visible={showEssentials} onHide={() => setShowEssentials(false)} />
        <ManageImportsDialog visible={showManageImports} onHide={() => setShowManageImports(false)} />
        <BodyStylesDialog visible={showBodyDialog} onHide={() => setShowBodyDialog(false)} />
    </>);
}
